import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'
import gql from 'graphql-tag'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { SchoolModel, SchoolsOrderBy } from '@/interfaces/school'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppSimpleDistrictSelector from '@/components/AppSimpleDistrictSelector.vue'
import AppRefreshAndExport from '@/components/AppRefreshAndExport.vue'
import AppSearchBtn from '@/components/AppSearchBtn.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppTextarea from '@/components/AppTextarea.vue'
import SchoolDialog from './script-dialog'
import ListPage from '@/mixins/list-page'
import { exportSchoolsList, findSchools } from './gql'
import { TypedGqlError } from '@/interfaces/error'
import { AreaModel } from '@/interfaces/area'
import { importExcel } from '@/utils/excel'
import UploadingJob from '@/mixins/uploading-job'
import AppLoading from '@/components/AppLoading.vue'
import { downloadBase64 } from '@/helpers/base64-to-file'

@Component({
  components: {
    AppBreadcrumbs,
    AppCheckbox,
    AppRefreshAndExport,
    AppDialog,
    AppSimpleTextField,
    AppSimpleSelector,
    AppDatePicker,
    AppSearchBtn,
    AppSimpleDistrictSelector,
    AppTextarea,
    AppLoading
  }
})

export default class SchoolList extends mixins(SchoolDialog, ListPage, UploadingJob) {
  @State(state => state.page.SchoolList) cache!: string

  breadcrumbs: Breadcrumb[] = [
    {
      text: '学校管理'
    },
    {
      text: '学校'
    }
  ]

  selectedScools: SchoolModel[] = []
  options = {
    sortBy: ['createdAt'],
    page: 1,
    itemsPerPage: 10,
    sortDesc: [true]
  }

  filter: {
    name: string;
    area: AreaModel | null;
  } = {
    name: '',
    area: null
  }

  totalNum = 0

  loading = false

  disableConfirm = false

  headers = [
    { text: '学校名称', value: 'name' },
    { text: '地区', value: 'area.mergerName', sortable: false },

    { text: '创建时间', value: 'createdAt' },
    { text: '创建人', value: 'managers.createdBy.username', sortable: false },
    { text: '操作', value: 'actions', sortable: false, align: 'end' }
  ]

  disabledActions = true

  schools: SchoolModel[] = []
  selectedSchools: SchoolModel[] = []

  windowSize = {
    w: 0,
    h: 0
  }

  showLoading = false

  actionsHeight = 51

  @Watch('options', { deep: true })
  onOptionsChanged () {
    this.getSchools()
  }

  mounted () {
    if (this.getRole() === 'dealer') {
      this.headers.pop()
    }
    this.checkIfJobRunnig()
    this.onResize()
    this.restorePage(this.cache)
    this.getSchools()
  }

  async refreshList () {
    this.filter = {
      name: '',
      area: null
    }
    this.getSchools()
  }

  async getSchools () {
    const { sortBy, page, itemsPerPage, sortDesc } = this.options
    const { area, name } = this.filter
    const by = SchoolsOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false

    this.loading = true
    const order = { by, asc }
    const paging = {
      skip: itemsPerPage * (page - 1),
      take: itemsPerPage
    }
    const where = {
      name: name || null,
      areaId: area?.id || null
    }
    const result = await this.querySchoolsRequest(order, paging, where)
    if (result) {
      this.schools = result.schools
      this.totalNum = result.paging.totalCount
    }
    this.loading = false
    this.storePage()
  }

  async querySchoolsRequest (order: any, paging: any, where: any) {
    try {
      const result = await this.$apollo.query({
        query: findSchools,
        variables: {
          order,
          paging,
          where
        }

      })
      return result.data.findSchools
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  async filterQuerySubmit () {
    await this.getSchools()
  }

  async deleteConfirm () {
    await this.deleteSchoolById()
    await this.getSchools()
  }

  async importSchoolRequest (dataB64: string, jobId: string) {
    try {
      const result = await this.$apollo.mutate({
        mutation: gql`
          mutation ( $dataB64: String!, $jobId: String!) {
            importSchools(
              dataB64: $dataB64,
              jobId: $jobId
            )
          }
        `,
        variables: {
          dataB64,
          jobId
        }
      })

      const res = result.data.importSchools
      if (res) {
        showNotification({
          msg: '导入学校成功',
          type: SnackType.Info
        })
        this.clearLoadingJob()
        this.getSchools()
      }
    } catch (error) {
      this.getRunningJob(jobId)
      showErrors(error.graphQLErrors)
    }
  }

  addFile () {
    importExcel(async (data: any) => {
      const dataB64 = Buffer.from(JSON.stringify(data)).toString('base64')
      const jobId = await this.createUploadingJob()
      this.showLoading = true
      this.importSchoolRequest(dataB64, jobId)
    })
  }

  downloadFile () {
    window.open(process.env.VUE_APP_OSS_URL + '/excel_samples/school_imports_sample.xlsx')
  }

  // 详情
  routeToDetail (item: any) {
    this.$router.push({
      name: 'SchoolDetail',
      params: {
        id: item.id,
        name: item.name
      }
    })
  }

  async exportHandler () {
    showNotification({
      msg: '即将开始下载...',
      type: SnackType.Info
    })
    const { sortBy, sortDesc } = this.options
    const {
      name, area
    } = this.filter
    const by = SchoolsOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false
    this.loading = true
    const order = { by, asc }
    const where = {
      name: name || null,
      areaId: area?.id || null
    }
    try {
      const result = await this.$apollo.query({
        query: exportSchoolsList,
        variables: {
          order,
          where
        }
      })
      if (result.data) {
        const { fileName, contentB64 } = result.data.exportSchoolsList
        downloadBase64(contentB64, fileName)
      }
      this.loading = false
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
    const actionDom = this.$refs.actions as Vue & {
      clientHeight: number;
    }
    this.actionsHeight = actionDom.clientHeight
  }
}
